import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { formatPKR, formatDate } from './format';
import {
  candidateAmount,
  candidateGrnDate,
  candidateGrnNo,
  candidateAuthStatus,
  candidateInvoiceRef,
  candidateLineId,
  candidatePoNos,
  candidateSelectionId,
  candidateStalePurchaseInvoiceId,
  candidateStaleReason,
  hasExplicitAlreadyInvoicedFlag,
  isAlreadyInvoiced,
  isStaleCandidate,
  isUnapprovedCandidate,
} from './grnCandidates';

export interface GRNCandidate {
  grn_id: number;
  grn_no: number | string;
  grn_date: string;
  grn_amount?: number | string | null;
  order_nos?: string;
  order_no?: string | number;
  invoice_count?: number;
  invoice_ref_no?: string | null;
  grn_det_id?: number | string | null;
  candidate_id?: string | null;
  remaining_amount?: number | string | null;
  already_invoiced?: boolean | string | null;
  stale_candidate?: boolean | string | null;
  stale_reason?: string | null;
  stale_purchase_invoice_id?: number | string | null;
  // Pass-through. The resume webhook may need any of these.
  [key: string]: unknown;
}

export interface LiveCandidateRefresh {
  refreshed_at?: string | null;
  message?: string | null;
  stale_message?: string | null;
  fallback?: string | null;
  [key: string]: unknown;
}

export interface GRNCandidatePickerProps {
  candidates: GRNCandidate[];
  invoiceSubtotal: number | null;
  invoiceGross?: number | null;
  recommendedGrnNos?: string[];
  recommendedGrnDetailIds?: Array<number | string>;
  liveRefresh?: LiveCandidateRefresh | null;
  selectedIds: number[];
  onChange: (selected: { ids: number[]; rows: GRNCandidate[] }) => void;
}

function candidatePriority(c: GRNCandidate): number {
  const value = c.match_priority ?? c.MATCH_PRIORITY;
  const priority = Number(value ?? 9);
  return Number.isFinite(priority) ? priority : 9;
}

function hasStableCandidateId(c: GRNCandidate): boolean {
  return (
    (c.candidate_id !== null && c.candidate_id !== undefined && c.candidate_id !== '') ||
    (c.CANDIDATE_ID !== null && c.CANDIDATE_ID !== undefined && c.CANDIDATE_ID !== '')
  );
}

function closeAmount(left: number, right: number): boolean {
  return Math.abs(left - right) < 0.5;
}

function candidateDetailIds(candidate: GRNCandidate): string[] {
  const raw = [
    candidate.grn_detail_ids,
    candidate.GRN_DETAIL_IDS,
    candidate.selected_grn_detail_ids,
    candidate.SELECTED_GRN_DETAIL_IDS,
    candidate.grn_det_id,
    candidate.GRN_DET_ID,
  ];
  const values = raw
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .flatMap((value) => String(value ?? '').split(','))
    .map((value) => value.trim())
    .filter(Boolean);
  return [...new Set(values)];
}

function splitCandidateCsv(value: string): string[] {
  if (value === '—') return [];
  return value
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

function numericDetailIds(candidate: GRNCandidate): number[] {
  return candidateDetailIds(candidate)
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0);
}

function findExactSubset(candidates: GRNCandidate[], target: number): GRNCandidate[] {
  // Keep exhaustive subset search bounded; the picker recomputes this on render.
  if (candidates.length > 16) return [];
  const totalMasks = 1 << candidates.length;
  let match: GRNCandidate[] = [];
  for (let mask = 1; mask < totalMasks; mask += 1) {
    const picked: GRNCandidate[] = [];
    const pickedDetailIds = new Set<string>();
    let sum = 0;
    let overlaps = false;
    for (let index = 0; index < candidates.length; index += 1) {
      if ((mask & (1 << index)) === 0) continue;
      const candidate = candidates[index];
      const detailIds = candidateDetailIds(candidate);
      for (const detailId of detailIds) {
        if (pickedDetailIds.has(detailId)) overlaps = true;
        pickedDetailIds.add(detailId);
      }
      picked.push(candidate);
      sum += candidateAmount(candidate);
    }
    if (overlaps) continue;
    if (!closeAmount(sum, target)) continue;
    if (match.length > 0) return [];
    match = picked;
  }
  return match;
}

function refreshMessage(liveRefresh: LiveCandidateRefresh): string {
  const primary =
    liveRefresh.message ??
    liveRefresh.stale_message ??
    'Oracle candidates were refreshed.';
  if (liveRefresh.fallback === 'extracted_po_no_rows_supplier_date') {
    return `${primary} Exact PO search returned no rows, so this list was broadened to supplier/date evidence.`;
  }
  return primary;
}

function deriveRecommendedSelectionIds(
  candidates: GRNCandidate[],
  targets: number[],
): Set<number> {
  const eligible = candidates.filter(
    (candidate) =>
      !isAlreadyInvoiced(candidate) &&
      !isStaleCandidate(candidate) &&
      !isUnapprovedCandidate(candidate) &&
      candidateAmount(candidate) > 0,
  );
  const ids = new Set<number>();
  const priorities = [...new Set(eligible.map(candidatePriority))].sort((a, b) => a - b);
  for (const priority of priorities) {
    const priorityCandidates = eligible.filter((c) => candidatePriority(c) === priority);
    const byEvidence = new Map<string, GRNCandidate[]>();
    for (const candidate of priorityCandidates) {
      const key = [
        candidateGrnNo(candidate),
        candidatePoNos(candidate),
        candidateLineId(candidate) === null ? 'total' : 'line',
      ].join('|');
      byEvidence.set(key, [...(byEvidence.get(key) ?? []), candidate]);
    }
    for (const target of targets) {
      const exactSingles = [...byEvidence.values()].flatMap((group) =>
        group.filter((candidate) => closeAmount(candidateAmount(candidate), target)),
      );
      if (exactSingles.length === 1) {
        ids.add(candidateSelectionId(exactSingles[0]));
        return ids;
      }
      if (exactSingles.length > 1) continue;
      const subset = findExactSubset(priorityCandidates, target);
      if (subset.length > 0) {
        subset.forEach((candidate) => ids.add(candidateSelectionId(candidate)));
        return ids;
      }
    }
  }
  return ids;
}

function collapseLegacyCandidates(candidates: GRNCandidate[]): GRNCandidate[] {
  const byGroup = new Map<string, GRNCandidate[]>();
  for (const candidate of candidates) {
    const key = String(candidate.grn_id ?? candidate.GRN_ID ?? candidate.grn_no ?? candidate.GRN_NO);
    byGroup.set(key, [...(byGroup.get(key) ?? []), candidate]);
  }

  const collapsed: GRNCandidate[] = [];
  for (const group of byGroup.values()) {
    if (group.length <= 1 || group.some(hasStableCandidateId)) {
      collapsed.push(...group);
      continue;
    }
    const first = group[0];
    const grnId = first.grn_id ?? first.GRN_ID;
    const detailIds = group
      .map(candidateLineId)
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (detailIds.length > 0) {
      collapsed.push(
        ...group.map((candidate) => {
          const lineId = candidateLineId(candidate);
          return {
            ...candidate,
            candidate_id: String(
              candidate.candidate_id ??
              candidate.CANDIDATE_ID ??
              `${grnId ?? candidateGrnNo(candidate)}:${lineId ?? candidateSelectionId(candidate)}`,
            ),
            grn_detail_ids: lineId ? [Number(lineId)] : candidate.grn_detail_ids,
            GRN_DETAIL_IDS: lineId ? [Number(lineId)] : candidate.GRN_DETAIL_IDS,
          };
        }),
      );
      continue;
    }
    collapsed.push(
      group.reduce((best, candidate) =>
        candidateAmount(candidate) > candidateAmount(best) ? candidate : best,
      ),
    );
  }
  return collapsed;
}

function candidateDisplayGroupKey(candidate: GRNCandidate): string {
  return [
    candidateDisplayGrnKey(candidate),
    candidateLineId(candidate) === null ? 'total' : 'detail',
  ].join('|');
}

function candidateDisplayGrnKey(candidate: GRNCandidate): string {
  return [
    candidate.grn_id ?? candidate.GRN_ID ?? candidateGrnNo(candidate),
    candidateGrnNo(candidate),
    isAlreadyInvoiced(candidate) ? 'invoiced' : 'open',
    isStaleCandidate(candidate) ? 'stale' : 'fresh',
    isUnapprovedCandidate(candidate) ? 'unapproved' : 'postable',
    candidateAuthStatus(candidate) ?? '',
  ].join('|');
}

function removeSingleDetailTotalDuplicates(candidates: GRNCandidate[]): GRNCandidate[] {
  const lineKeys = new Set<string>();
  for (const candidate of candidates) {
    const lineId = candidateLineId(candidate);
    if (lineId === null) continue;
    lineKeys.add(
      [
        candidateDisplayGrnKey(candidate),
        String(lineId),
        candidateAmount(candidate).toFixed(2),
      ].join('|'),
    );
  }
  return candidates.filter((candidate) => {
    if (candidateLineId(candidate) !== null) return true;
    const detailIds = numericDetailIds(candidate);
    if (detailIds.length !== 1) return true;
    const key = [
      candidateDisplayGrnKey(candidate),
      String(detailIds[0]),
      candidateAmount(candidate).toFixed(2),
    ].join('|');
    return !lineKeys.has(key);
  });
}

function canGroupDetailRows(
  group: GRNCandidate[],
  recommendedDetailSet: Set<string>,
  autoRecommendedSelectionIds: Set<number>,
): boolean {
  if (group.length <= 1) return false;
  if (group.some((candidate) => candidateLineId(candidate) === null)) return false;

  const detailIds = group.flatMap(candidateDetailIds);
  if (detailIds.length < group.length) return false;
  if (new Set(detailIds).size !== detailIds.length) return false;

  const explicitlyRecommendedCount = detailIds.filter((id) =>
    recommendedDetailSet.has(id),
  ).length;
  if (explicitlyRecommendedCount === detailIds.length) return true;
  if (explicitlyRecommendedCount > 0) return false;

  const autoRecommendedCount = group.filter((candidate) =>
    autoRecommendedSelectionIds.has(candidateSelectionId(candidate)),
  ).length;
  if (autoRecommendedCount === group.length) return true;
  if (autoRecommendedCount > 0) return false;

  return true;
}

function groupDetailRowsForDisplay(
  candidates: GRNCandidate[],
  recommendedDetailSet: Set<string>,
  autoRecommendedSelectionIds: Set<number>,
): GRNCandidate[] {
  const byGroup = new Map<string, GRNCandidate[]>();
  for (const candidate of candidates) {
    const key = candidateDisplayGroupKey(candidate);
    byGroup.set(key, [...(byGroup.get(key) ?? []), candidate]);
  }

  const display: GRNCandidate[] = [];
  for (const group of byGroup.values()) {
    if (!canGroupDetailRows(group, recommendedDetailSet, autoRecommendedSelectionIds)) {
      display.push(...group);
      continue;
    }

    const first = group[0];
    const detailIds = [...new Set(group.flatMap(numericDetailIds))].sort((a, b) => a - b);
    const amount = group.reduce((sum, candidate) => sum + candidateAmount(candidate), 0);
    const poNos = [
      ...new Set(group.flatMap((candidate) => splitCandidateCsv(candidatePoNos(candidate)))),
    ];
    const invoiceRefs = [
      ...new Set(group.flatMap((candidate) => splitCandidateCsv(candidateInvoiceRef(candidate)))),
    ];
    display.push({
      ...first,
      candidate_id: `${first.grn_id ?? first.GRN_ID ?? candidateGrnNo(first)}:${detailIds[0]}`,
      grn_det_id: null,
      GRN_DET_ID: null,
      grn_detail_id: null,
      GRN_DETAIL_ID: null,
      grn_detail_ids: detailIds,
      GRN_DETAIL_IDS: detailIds,
      amount,
      AMOUNT: amount,
      grn_amount: amount,
      GRN_AMOUNT: amount,
      order_nos: poNos.join(', '),
      ORDER_NOS: poNos.join(', '),
      po_no: poNos.join(', '),
      PO_NO: poNos.join(', '),
      invoice_ref_no: invoiceRefs.join(', '),
      grouped_candidate_count: group.length,
      grouped_candidates: group,
    });
  }

  const groupedDetailKeys = new Set(
    display
      .filter((candidate) => Number(candidate.grouped_candidate_count ?? 0) > 1)
      .map(candidateDisplayGrnKey),
  );
  return display.filter((candidate) => {
    if (Number(candidate.grouped_candidate_count ?? 0) > 1) return true;
    if (!groupedDetailKeys.has(candidateDisplayGrnKey(candidate))) return true;
    return numericDetailIds(candidate).length <= 1;
  });
}

export function GRNCandidatePicker({
  candidates,
  invoiceSubtotal,
  invoiceGross = null,
  recommendedGrnNos = [],
  recommendedGrnDetailIds = [],
  liveRefresh = null,
  selectedIds,
  onChange,
}: GRNCandidatePickerProps) {
  const availableCandidates = useMemo(
    () =>
      collapseLegacyCandidates(
        candidates.filter((c) =>
          Number.isFinite(candidateSelectionId(c)) &&
          (!isAlreadyInvoiced(c) ||
            hasExplicitAlreadyInvoicedFlag(c) ||
            isStaleCandidate(c) ||
            isUnapprovedCandidate(c)),
        ),
      ),
    [candidates],
  );
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const recommendedSet = useMemo(
    () => new Set(recommendedGrnNos.map((id) => String(id).trim()).filter(Boolean)),
    [recommendedGrnNos],
  );
  const recommendedDetailSet = useMemo(
    () =>
      new Set(
        recommendedGrnDetailIds.map((id) => String(id).trim()).filter(Boolean),
      ),
    [recommendedGrnDetailIds],
  );
  const autoRecommendedSelectionIds = useMemo(() => {
    const targets = [invoiceGross, invoiceSubtotal].filter(
      (value): value is number => value !== null && Number.isFinite(value),
    );
    return deriveRecommendedSelectionIds(availableCandidates, targets);
  }, [availableCandidates, invoiceGross, invoiceSubtotal]);
  const displayCandidates = useMemo(
    () =>
      groupDetailRowsForDisplay(
        removeSingleDetailTotalDuplicates(availableCandidates),
        recommendedDetailSet,
        autoRecommendedSelectionIds,
      ),
    [availableCandidates, recommendedDetailSet, autoRecommendedSelectionIds],
  );

  const selectedRows = useMemo(
    () => displayCandidates.filter((c) => selectedSet.has(candidateSelectionId(c))),
    [displayCandidates, selectedSet],
  );

  const selectedTotal = useMemo(
    () => selectedRows.reduce((sum, r) => sum + candidateAmount(r), 0),
    [selectedRows],
  );

  const subtotal = invoiceSubtotal ?? null;
  const gross = invoiceGross ?? null;
  const matchesSubtotal = subtotal !== null && closeAmount(selectedTotal, subtotal);
  const matchesGross = gross !== null && closeAmount(selectedTotal, gross);
  const target = matchesSubtotal ? subtotal : matchesGross ? gross : subtotal;
  const targetBasis: 'subtotal' | 'gross' | null = matchesSubtotal
    ? 'subtotal'
    : matchesGross
      ? 'gross'
      : subtotal !== null
        ? 'subtotal'
        : null;
  const delta = target == null ? null : Number((selectedTotal - target).toFixed(2));
  const subtotalDelta =
    subtotal == null ? null : Number((selectedTotal - subtotal).toFixed(2));
  const grossDelta =
    gross == null ? null : Number((selectedTotal - gross).toFixed(2));
  const matched = delta != null && Math.abs(delta) < 0.5;

  const toggle = (cand: GRNCandidate, checked: boolean) => {
    if (isAlreadyInvoiced(cand) || isStaleCandidate(cand) || isUnapprovedCandidate(cand)) return;
    const next = new Set(selectedSet);
    const id = candidateSelectionId(cand);
    if (checked) next.add(id);
    else next.delete(id);
    const ids = Array.from(next);
    const rows = displayCandidates.filter((c) => next.has(candidateSelectionId(c)));
    onChange({ ids, rows });
  };

  return (
    <div className="flex flex-col gap-8">
      {liveRefresh && (
        <div className="rounded-md border border-warning-500/30 bg-warning-50 px-12 py-8 text-sm text-warning-900">
          <p className="font-medium">Live candidate refresh</p>
          <p className="mt-2">
            {refreshMessage(liveRefresh)}
            {liveRefresh.refreshed_at ? ` ${formatDate(liveRefresh.refreshed_at)}.` : ''}
          </p>
        </div>
      )}
      <div className="overflow-x-auto rounded-md border border-neutral-200">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-left text-xs font-medium uppercase tracking-wide text-neutral-600">
            <tr>
              <th className="w-32 px-8 py-6"> </th>
              <th className="px-8 py-6">GRN #</th>
              <th className="px-8 py-6">GRN date</th>
              <th className="px-8 py-6">PO #</th>
              <th className="px-8 py-6 text-right">GRN amount</th>
              <th className="px-8 py-6">Invoice ref</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {displayCandidates.map((c) => {
              const selectionId = candidateSelectionId(c);
              const grnNo = candidateGrnNo(c);
              const lineId = candidateLineId(c);
              const isSelected = selectedSet.has(selectionId);
              const isRecommended =
                recommendedSet.has(grnNo) ||
                candidateDetailIds(c).some((id) => recommendedDetailSet.has(id)) ||
                autoRecommendedSelectionIds.has(selectionId);
              const alreadyInvoiced = isAlreadyInvoiced(c);
              const stale = isStaleCandidate(c);
              const unapproved = isUnapprovedCandidate(c);
              const blocked = alreadyInvoiced || stale || unapproved;
              const lineLabel = lineId ? ` line ${lineId}` : '';
              const staleReason = candidateStaleReason(c);
              const stalePiId =
                stale || alreadyInvoiced ? candidateStalePurchaseInvoiceId(c) : null;
              const groupedCandidateCount = Number(c.grouped_candidate_count ?? 0);
              return (
                <tr
                  key={selectionId}
                  className={cn(
                    blocked && 'bg-neutral-50 text-neutral-500',
                    isRecommended && !isSelected && 'bg-success-50/50',
                    isSelected && 'bg-primary-50',
                  )}
                >
                  <td className="px-8 py-6">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={blocked}
                      onChange={(e) => toggle(c, e.target.checked)}
                      aria-label={`Select GRN ${grnNo}${lineLabel}`}
                    />
                  </td>
                  <td className="px-8 py-6">
                    <div className="flex items-center gap-8">
                      <span className="font-mono">{grnNo}</span>
                      {groupedCandidateCount > 1 && (
                        <span className="rounded-full border border-neutral-300 bg-neutral-50 px-6 py-1 text-[11px] font-semibold uppercase tracking-wide text-neutral-600">
                          {groupedCandidateCount} lines
                        </span>
                      )}
                      {isRecommended && (
                        <span className="rounded-full border border-success-500/30 bg-success-50 px-6 py-1 text-[11px] font-semibold uppercase tracking-wide text-success-700">
                          Recommended
                        </span>
                      )}
                      {stale && (
                        <span className="rounded-full border border-warning-500/30 bg-warning-50 px-6 py-1 text-[11px] font-semibold uppercase tracking-wide text-warning-800">
                          Stale
                        </span>
                      )}
                      {unapproved && (
                        <span className="rounded-full border border-amber-500/30 bg-amber-50 px-6 py-1 text-[11px] font-semibold uppercase tracking-wide text-amber-700">
                          {candidateAuthStatus(c) ?? 'Awaiting approval'}
                        </span>
                      )}
                      {alreadyInvoiced && (
                        <span className="rounded-full border border-error-500/30 bg-error-50 px-6 py-1 text-[11px] font-semibold uppercase tracking-wide text-error-700">
                          Already invoiced
                        </span>
                      )}
                    </div>
                    {(staleReason || stalePiId) && (
                      <p className="mt-2 text-xs text-neutral-500">
                        {[staleReason, stalePiId ? `PI ${stalePiId}` : null]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                    )}
                  </td>
                  <td className="px-8 py-6">{formatDate(candidateGrnDate(c))}</td>
                  <td className="px-8 py-6">{candidatePoNos(c)}</td>
                  <td className="px-8 py-6 text-right">{formatPKR(candidateAmount(c))}</td>
                  <td className="px-8 py-6 text-xs text-neutral-600">
                    {candidateInvoiceRef(c)}
                  </td>
                </tr>
              );
            })}
            {displayCandidates.length === 0 && (
              <tr>
                <td className="px-8 py-12 text-center text-neutral-500" colSpan={6}>
                  No uninvoiced GRN candidates returned by Oracle.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div
        className={cn(
          'flex flex-wrap items-center justify-between gap-12 rounded-md border px-12 py-8 text-sm',
          matched
            ? 'border-success-500 bg-success-50 text-success-700'
            : 'border-neutral-200 bg-neutral-50 text-neutral-700',
        )}
      >
        <span>
          Selected total:{' '}
          <span className="font-semibold">{formatPKR(selectedTotal)}</span>
        </span>
        <span>
          Invoice target:{' '}
          <span className="font-semibold">
            {target == null ? '—' : formatPKR(target)}
          </span>
          {targetBasis && target != null ? (
            <span className="ml-4 text-xs text-neutral-500">
              ({targetBasis === 'gross' ? 'gross (incl tax)' : 'subtotal'})
            </span>
          ) : null}
        </span>
        {matched ? (
          <span>
            Δ <span className="font-semibold">{formatPKR(delta!)}</span> ✓
          </span>
        ) : (
          <span className="flex flex-wrap items-center gap-12">
            <span>
              Δ vs subtotal:{' '}
              <span className="font-semibold">
                {subtotalDelta == null ? '—' : formatPKR(subtotalDelta)}
              </span>
            </span>
            <span>
              Δ vs gross:{' '}
              <span className="font-semibold">
                {grossDelta == null ? '—' : formatPKR(grossDelta)}
              </span>
            </span>
          </span>
        )}
      </div>
    </div>
  );
}
